import { useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import FeatureFeedbackHandler from 'handlers/feature-feedback.handler';
import { useAppSelector } from 'store/hook';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import { useTranslation } from 'react-i18next';

interface FeedbackWidgetProps {
  featureName: string;
  metadata: Record<string, string>;
  label?: string;
}

export default function FeedbackWidget({
  featureName,
  metadata,
  label = 'Was this helpful?',
}: FeedbackWidgetProps) {
  const { t } = useTranslation();
  const { email: userEmail } = useAppSelector((state) => state.authReducer);
  const [feedback, setFeedback] = useState<
    'like' | 'dislike' | 'loading' | null
  >(null);
  const [dislikeAnchorEl, setDislikeAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [dislikeComment, setDislikeComment] = useState('');
  const feedbackHandler = useRef(new FeatureFeedbackHandler());

  const submitFeedback = async (
    value: 'like' | 'dislike',
    comment?: string
  ) => {
    if (feedback !== null) return;
    setFeedback('loading');
    await feedbackHandler.current.submitFeedback({
      userEmail,
      featureName,
      value,
      source: 'web',
      metadata: comment ? { ...metadata, comment } : metadata,
    });
    setFeedback(value);
  };

  const handleDislikeClick = (e: React.MouseEvent<HTMLElement>) => {
    setDislikeAnchorEl(e.currentTarget);
  };

  const handleDislikePopoverClose = (shouldSubmit: boolean) => {
    setDislikeAnchorEl(null);
    void submitFeedback('dislike', shouldSubmit ? dislikeComment : undefined);
    setDislikeComment('');
  };

  return (
    <CustomMuiThemeProvider>
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        sx={{
          px: 2,
          py: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor:
            feedback === 'like' || feedback === 'dislike'
              ? 'transparent'
              : 'divider',
          background:
            feedback === 'like' || feedback === 'dislike'
              ? 'transparent'
              : 'action.hover',
          transition: 'all 0.3s ease',
        }}
      >
        {feedback === 'like' || feedback === 'dislike' ? (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            {t('thanks_for_your_feedback')}
          </Typography>
        ) : (
          <>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
            >
              {label}
            </Typography>
            {feedback === 'loading' ? (
              <CircularProgress size={18} sx={{ ml: 0.5 }} />
            ) : (
              <>
                <Tooltip title={t('yes_helpful')} placement="top">
                  <IconButton
                    size="small"
                    onClick={() => {
                      void submitFeedback('like');
                    }}
                    sx={{
                      color: 'text.secondary',
                      transition: 'color 0.2s, transform 0.15s',
                      '&:hover': {
                        color: '#4caf50',
                        transform: 'scale(1.2)',
                        background: 'rgba(76,175,80,0.1)',
                      },
                    }}
                  >
                    <ThumbUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('not_helpful')} placement="top">
                  <IconButton
                    size="small"
                    onClick={handleDislikeClick}
                    sx={{
                      color: 'text.secondary',
                      transition: 'color 0.2s, transform 0.15s',
                      '&:hover': {
                        color: '#ef6b6b',
                        transform: 'scale(1.2)',
                        background: 'rgba(239,107,107,0.1)',
                      },
                    }}
                  >
                    <ThumbDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Popover
                  open={Boolean(dislikeAnchorEl)}
                  anchorEl={dislikeAnchorEl}
                  onClose={() => {
                    handleDislikePopoverClose(false);
                  }}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                  PaperProps={{
                    sx: {
                      p: 2.5,
                      width: 300,
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}
                  >
                    {t('what_could_be_better')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}
                  >
                    {t('optional_your_feedback_helps_us_improve')}
                  </Typography>
                  <TextField
                    autoFocus
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={t('tell_us_what_went_wrong')}
                    value={dislikeComment}
                    onChange={(e) => {
                      setDislikeComment(e.target.value);
                    }}
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ color: 'text.secondary' }}
                      onClick={() => {
                        handleDislikePopoverClose(false);
                      }}
                    >
                      Skip
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        handleDislikePopoverClose(true);
                      }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Popover>
              </>
            )}
          </>
        )}
      </Box>
    </CustomMuiThemeProvider>
  );
}
